import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Nav $scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">Chandelier Luxe</Logo>

        <MenuIcon onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MenuIcon>

        <NavLinks $isOpen={isOpen}>
          <NavItem>
            <NavLink
              to="/"
              $active={location.pathname === "/"}
              onClick={() => setIsOpen(false)}
            >
              Home
              {location.pathname === "/" && <ActiveLine layoutId="underline" />}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="/products"
              $active={location.pathname === "/products"}
              onClick={() => setIsOpen(false)}
            >
              Products
              {location.pathname === "/products" && (
                <ActiveLine layoutId="underline" />
              )}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              to="/about"
              $active={location.pathname === "/about"}
              onClick={() => setIsOpen(false)}
            >
              About
              {location.pathname === "/about" && (
                <ActiveLine layoutId="underline" />
              )}
            </NavLink>
          </NavItem>
          <NavItem>
            <ContactButton to="/contact">Contact</ContactButton>
          </NavItem>
        </NavLinks>
      </NavContainer>
    </Nav>
  );
};

const Nav = styled.nav`
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background: ${(props) =>
    props.$scrolled ? "rgba(26, 26, 26, 0.95)" : "transparent"};
  backdrop-filter: ${(props) => (props.$scrolled ? "blur(10px)" : "none")};
  box-shadow: ${(props) =>
    props.$scrolled ? "0 4px 30px rgba(0, 0, 0, 0.1)" : "none"};
`;

const NavContainer = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (min-width: 1024px) {
    justify-content: center;
    gap: 4rem;
  }
`;

const Logo = styled(Link)`
  font-family: "Cormorant Garamond", serif;
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.accent};
  text-decoration: none;
  z-index: 2;
`;

const MenuIcon = styled.div`
  display: none;
  cursor: pointer;
  font-size: 1.5rem;
  z-index: 2;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    background: rgba(26, 26, 26, 0.98);
    backdrop-filter: blur(10px);
    transform: ${(props) =>
      props.$isOpen ? "translateX(0)" : "translateX(100%)"};
    transition: transform 0.3s ease-in-out;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-decoration: none;
  color: ${(props) =>
    props.$active ? props.theme.colors.accent : props.theme.colors.text};
  transition: color 0.3s ease;
  padding: 0.5rem 0;
  position: relative;

  &:hover {
    color: ${(props) => props.theme.colors.accent};
  }
`;

const ActiveLine = styled(motion.div)`
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: ${(props) => props.theme.colors.accent};
`;

const ContactButton = styled(Link)`
  padding: 0.8rem 1.5rem;
  border: 2px solid ${(props) => props.theme.colors.accent};
  border-radius: 4px;
  color: ${(props) => props.theme.colors.accent};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => props.theme.colors.accent};
    color: ${(props) => props.theme.colors.primary};
  }
`;

export default Navbar;
